// Controls both steps of the spreadsheet mapping share: the Association
// dropdown, a filterable pick list, the class dialog, the code rule table and
// the value editor. One place, so the two steps read and behave alike.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Triangle } from 'lucide-react';
import type { OntologySchema } from '@/lib/useOntology';
import {
  isCorePredicate,
  matchedClass,
  matchedProperty,
  mintedPredicate,
  propertyShape,
  type CodeMeaningRow,
} from '@/lib/columnMapping';
import { cn } from '@/lib/utils';
import { inputCls } from '@/components/import/mappingStyle';
import { Message, messageClass } from '@/components/ui/Message';
import { Button, buttonVariants } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

/** The most entries a pick list shows before asking for a filter. */
const LIST_LIMIT = 50;

export function Field({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <label className={cn('block', className)}>
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}



/** One Association dropdown: its name, the answer under it, and a triangle
 *  that is filled once answered and hollow while still to choose. A dropdown
 *  that does not apply to the row says why instead of opening. */
export function AssocMenu({
  label,
  value,
  answered,
  disabledNote,
  invalid,
  children,
}: {
  label: string;
  value: string;
  answered: boolean;
  /** Set when the dropdown does not apply: the reason, shown in its place. */
  disabledNote?: string | undefined;
  invalid?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // Where the menu is drawn: fixed to the window, beside the button, so the
  // table's scrolling area never cuts it off (UAT 2026-09-28: Choose Class on
  // a low row opened into a clipped list that could not be reached).
  const [place, setPlace] = useState<{ top?: number; bottom?: number; right: number; maxHeight: number } | null>(
    null,
  );
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const openMenu = () => {
    const r = button.current?.getBoundingClientRect();
    if (r) {
      const below = window.innerHeight - r.bottom - 8;
      const above = r.top - 8;
      const right = Math.max(8, window.innerWidth - r.right);
      setPlace(
        below >= 240 || below >= above
          ? { top: r.bottom + 4, right, maxHeight: below }
          : { bottom: window.innerHeight - r.top + 4, right, maxHeight: above },
      );
    }
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return;
    // A menu fixed to the window would drift from its button as the page
    // scrolls, so any scroll or resize closes it.
    const onMove = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const disabled = disabledNote !== undefined;
  return (
    <div ref={ref} className="relative">
      <button
        ref={button}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        aria-label={label}
        onClick={() => (open ? setOpen(false) : openMenu())}
        className={cn(
          'flex w-full items-start justify-between gap-2 rounded border px-1.5 py-0.5 text-left text-xs',
          invalid ? 'border-tres-error' : 'border-transparent hover:border-border',
          disabled && 'cursor-default text-muted-foreground',
        )}
      >
        <span className="min-w-0">
          <span className="block">{label}</span>
          <span className="block truncate font-mono text-muted-foreground">
            {disabled ? disabledNote : value || 'not chosen'}
          </span>
        </span>
        {!disabled && (
          <Triangle
            aria-hidden
            className="mt-0.5 h-3 w-3 shrink-0 rotate-180"
            fill={answered ? 'currentColor' : 'none'}
          />
        )}
      </button>
      {open && (
        <div
          className="fixed z-50 w-72 overflow-y-auto rounded-md border border-border bg-card p-2 text-xs shadow-lg"
          style={place ?? undefined}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

/** A list inside a dropdown, with a filter once it is long. */
export function PickList<T>({
  title,
  items,
  keyOf,
  labelOf,
  onPick,
  empty,
}: {
  title: string;
  items: T[];
  keyOf: (t: T) => string;
  labelOf: (t: T) => string;
  onPick: (t: T) => void;
  empty: string;
}) {
  const [filter, setFilter] = useState('');
  const f = filter.trim().toLowerCase();
  const shown = items.filter((t) => !f || labelOf(t).toLowerCase().includes(f) || keyOf(t).toLowerCase().includes(f));
  return (
    <div className="mb-2">
      <div className="mb-1 font-medium">{title}</div>
      {items.length > 8 && (
        <input
          type="text"
          value={filter}
          placeholder="Filter"
          aria-label={`Filter ${title}`}
          onChange={(e) => setFilter(e.target.value)}
          className={cn(inputCls, 'mb-1')}
        />
      )}
      {shown.length === 0 ? (
        <div className="text-muted-foreground">{empty}</div>
      ) : (
        <ul className="max-h-40 overflow-y-auto">
          {shown.slice(0, LIST_LIMIT).map((t) => (
            <li key={keyOf(t)}>
              <button
                type="button"
                onClick={() => onPick(t)}
                className="w-full rounded px-1 py-0.5 text-left hover:bg-muted"
              >
                {labelOf(t)}
              </button>
            </li>
          ))}
          {shown.length > LIST_LIMIT && (
            <li className="px-1 text-muted-foreground">
              {shown.length - LIST_LIMIT} more — filter to find them.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}


export function PredicateHint({
  schema,
  text,
  column,
}: {
  schema: OntologySchema | null | undefined;
  text: string;
  column: string;
}) {
  if (!schema) return null;
  const effective = text.trim() || mintedPredicate(column);
  // Core-language predicates (`:comment`, `:label`, …) are part of the QROS
  // spec, not the dataset's ontology — they are always available and never
  // "created by importing", so they read quiet, like a declared term.
  if (isCorePredicate(effective)) {
    return (
      <span className="mt-0.5 block text-xs text-muted-foreground">
        Core term — part of the QROS language{!text.trim() ? ` · blank uses ${effective}` : ''}
      </span>
    );
  }
  const match = matchedProperty(schema, effective);
  if (match) {
    const shape = propertyShape(match);
    return (
      <span className="mt-0.5 block text-xs text-muted-foreground">
        Declared term{match.label ? ` · ${match.label}` : ''}
        {shape ? ` · ${shape}` : ''}
        {!text.trim() ? ` · blank uses ${effective}` : ''}
      </span>
    );
  }
  return (
    <span className="mt-0.5 block text-xs text-tres-warning">
      {text.trim()
        ? 'New term — the ontology does not declare it; importing creates it.'
        : `Blank creates ${effective} from the header — a new term.`}
    </span>
  );
}

/** Same signal for class fields. Blank is a designed state (untyped records),
 *  so it reads quiet, not as a warning. */
export function ClassHint({
  schema,
  text,
  blankNote,
}: {
  schema: OntologySchema | null | undefined;
  text: string;
  blankNote: string;
}) {
  if (!schema) return null;
  if (!text.trim()) {
    return <span className="mt-0.5 block text-xs text-muted-foreground">{blankNote}</span>;
  }
  const match = matchedClass(schema, text);
  if (match) {
    return (
      <span className="mt-0.5 block text-xs text-muted-foreground">
        Declared class{match.label ? ` · ${match.label}` : ''}
        {match.super_classes.length > 0 ? ` · under ${match.super_classes.join(', ')}` : ''}
      </span>
    );
  }
  return (
    <span className="mt-0.5 block text-xs text-tres-warning">
      Not a declared class — use Create to say what kind of thing it is.
    </span>
  );
}

export function CreateClassDialog({
  schema,
  suggested,
  onCancel,
  onCreate,
}: {
  schema: OntologySchema | null | undefined;
  suggested: string;
  onCancel: () => void;
  onCreate: (created: { uri: string; label: string; parent: string }) => void;
}) {
  const [name, setName] = useState(suggested.trim());
  const [parent, setParent] = useState('');
  const uri = name.trim().startsWith(':') ? name.trim() : `:${name.trim().replace(/\W+/g, '')}`;
  // A class the ontology already has is chosen, not created again: creating
  // it would declare it a second time, perhaps under another parent.
  const existing = name.trim() ? schema?.classes.find((c) => c.uri === uri) : undefined;
  const ready = name.trim().length > 0 && parent.trim().length > 0 && !existing;
  return (
    <Modal
      title="Create a new class"
      subtitle="The import will add this class to your ontology. Say what kind of thing it is, so everything under it can be found and reasoned about."
      size="md"
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready}
            onClick={() => onCreate({ uri, label: name.trim().replace(/^:/, ''), parent: parent.trim() })}
          >
            Create class
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <Field label="Class Name">
          <input
            autoFocus
            type="text"
            value={name}
            placeholder="Player"
            onChange={(e) => setName(e.target.value)}
            className={cn(inputCls, 'font-mono')}
          />
          {name.trim() && !existing && (
            <span className="mt-0.5 block text-xs text-muted-foreground">Created as {uri}</span>
          )}
          {existing && (
            <Message kind="warning" density="compact" className="mt-1">
              Your ontology already has {uri}
              {existing.super_classes[0] ? `, under ${existing.super_classes[0]}` : ''}. Cancel and choose it
              from the Ontology list, or give the new class another name.
            </Message>
          )}
        </Field>
        <Field label="Sub Class Of">
          <input
            type="text"
            list="tres-onto-classes"
            value={parent}
            placeholder=":Concept"
            onChange={(e) => setParent(e.target.value)}
            className={cn(inputCls, 'font-mono')}
          />
          <ClassHint
            schema={schema}
            text={parent}
            blankNote="Required. A class with no parent is an orphan: nothing can say what it is or what else is like it."
          />
        </Field>
        {!ready && (
          <div className={messageClass('guidance', 'compact')}>
            Both are needed. Pick the parent from the classes your ontology already declares —
            <em> Vocabulary</em> (<span className="font-mono">:Vocabulary</span>) for a list of terms such as
            positions or codes, or whichever class this one sits under.
          </div>
        )}
      </div>
    </Modal>
  );
}

/** The rule behind a code whose values SAY something: one row per value, with
 *  the statement it makes. Everything not listed says nothing. */
export function CodeRuleEditor({
  rows,
  onChange,
  schema,
}: {
  rows: CodeMeaningRow[];
  onChange: (next: CodeMeaningRow[]) => void;
  schema: OntologySchema | null | undefined;
}) {
  const set = (i: number, patch: Partial<CodeMeaningRow>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="w-full space-y-1">
      {rows.length === 0 && (
        <div className={messageClass('guidance', 'compact')}>
          No rule yet, so this column says nothing. Add a value and what it means — for example{' '}
          <span className="font-mono">Y</span> means the record is{' '}
          <span className="font-mono">:MajorDefenseEquipment</span>.
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-start gap-1">
          <input
            type="text"
            value={r.value}
            placeholder="Y"
            onChange={(e) => set(i, { value: e.target.value })}
            className={cn(inputCls, 'w-20 font-mono')}
            aria-label="Cell value"
          />
          <span className="pt-1 text-xs text-muted-foreground">means the record</span>
          <div className="w-40">
            <input
              type="text"
              list="tres-onto-props"
              value={r.predicate}
              placeholder=":type"
              onChange={(e) => set(i, { predicate: e.target.value })}
              className={cn(inputCls, 'font-mono')}
              aria-label="Statement"
            />
            <PredicateHint schema={schema} text={r.predicate} column="" />
          </div>
          <input
            type="text"
            list={r.objectIsIri ? 'tres-onto-classes' : undefined}
            value={r.object}
            placeholder=":MajorDefenseEquipment"
            onChange={(e) => set(i, { object: e.target.value })}
            className={cn(inputCls, 'w-56 font-mono')}
            aria-label="What it means"
          />
          <label className="flex items-center gap-1 pt-1 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={!r.objectIsIri}
              onChange={(e) => set(i, { objectIsIri: !e.target.checked })}
            />
            as text
          </label>
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
            aria-label="Remove this meaning"
          >
            ×
          </button>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() =>
          onChange([...rows, { value: '', predicate: ':type', object: '', objectIsIri: true }])
        }
      >
        + what a value means
      </Button>
      <p className="text-xs text-muted-foreground">
        A value you have not listed asserts nothing. A blank cell, or an{' '}
        <span className="font-mono">N</span> you leave out, is silence — not a recorded &ldquo;no&rdquo;.
      </p>
    </div>
  );
}

export function ReplacePairsEditor({
  pairs,
  onChange,
}: {
  pairs: { from: string; to: string }[];
  onChange: (next: { from: string; to: string }[]) => void;
}) {
  return (
    <div className="mt-1 space-y-1">
      {pairs.map((p, pi) => (
        <div key={pi} className="flex items-center gap-1">
          <input
            type="text"
            value={p.from}
            placeholder="replace"
            onChange={(e) => onChange(pairs.map((q, qi) => (qi === pi ? { ...q, from: e.target.value } : q)))}
            className={cn(inputCls, 'w-24')}
          />
          <span className="text-muted-foreground">→</span>
          <input
            type="text"
            value={p.to}
            placeholder="with"
            onChange={(e) => onChange(pairs.map((q, qi) => (qi === pi ? { ...q, to: e.target.value } : q)))}
            className={cn(inputCls, 'w-24')}
          />
          <button
            type="button"
            onClick={() => onChange(pairs.filter((_, qi) => qi !== pi))}
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
            aria-label="Remove transform"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...pairs, { from: '', to: '' }])}
        className={buttonVariants({ variant: 'secondary', size: 'sm' })}
      >
        + transform
      </button>
    </div>
  );
}
